import { isNull, orderBy } from "lodash-es";
import { DateTime } from "luxon";
import {
  SessionEvent,
  IMDSGroupedMessage,
  AnySessionPart,
  SessionPartKind,
  MDSSessionConfig,
  SessionFeature,
} from "./session.types";
import { SessionItem } from "@/mds/components/sessions/session-item";

/**
 * Sorts events by their creation timestamp using luxon DateTime
 */
export const sortEventsByTimestamp = (events: SessionEvent[]): SessionEvent[] => {
  return orderBy(events, [event => DateTime.fromISO(event.locally_created_at).toMillis()], ["asc"]);
};

export const hasContentInParts = (parts: AnySessionPart[]): boolean => {
  return parts.some(part => {
    switch (part.kind) {
      case SessionPartKind.MarkdownV1:
        return Boolean(part.content);
      case SessionPartKind.ReasoningV1:
        return Boolean(part.summary);
      case SessionPartKind.InterruptV1:
        return Boolean(part.summary);
      case SessionPartKind.WebClipV1:
        return true;
      default:
        return false;
    }

    return false;
  });
};

export const hasNonEmptyParts = (event: SessionEvent): boolean => {
  const parts = (event.data.value?.parts as AnySessionPart[]) ?? [];
  return parts.length > 0 && hasContentInParts(parts);
};

/**
 * Groups message events by type, specifically handling reasoning events
 */
export const groupEventsByType = (events: SessionEvent[]): IMDSGroupedMessage[] => {
  const groupedMessages: IMDSGroupedMessage[] = [];
  let currentReasoningGroup: SessionEvent[] = [];

  events.forEach(event => {
    // Skip events with empty parts
    if (!hasNonEmptyParts(event)) {
      return;
    }

    const isReasoningEvent = event.data.kind === "AGENT_OUTPUT";

    if (isReasoningEvent) {
      currentReasoningGroup.push(event);
    } else {
      // If we have accumulated reasoning events, group them
      if (currentReasoningGroup.length > 0) {
        groupedMessages.push({
          type: "reasoning_group",
          events: [...currentReasoningGroup],
        });
        currentReasoningGroup = [];
      }

      // Add the non-reasoning event as a single event
      groupedMessages.push({
        type: "single",
        event,
      });
    }
  });

  // Add any remaining reasoning events
  if (currentReasoningGroup.length > 0) {
    groupedMessages.push({
      type: "reasoning_group",
      events: [...currentReasoningGroup],
    });
  }

  return groupedMessages;
};

/**
 * Main function to sort and group events
 */
export const processSessionEvents = (events: SessionEvent[]): IMDSGroupedMessage[] => {
  const sortedEvents = sortEventsByTimestamp(events);
  return groupEventsByType(sortedEvents);
};

export const sessionPartContainsImage = (part: AnySessionPart): boolean => {
  switch (part.kind) {
    case SessionPartKind.MarkdownV1:
      return false;
    case SessionPartKind.ReasoningV1:
      return false;
    case SessionPartKind.WebClipV1:
      return !isNull(part.clipped_image_uploaded_file_url);
    default:
      return false;
  }
};

export const getWebClipFromSessionItems = ({
  sessionItems,
}: {
  sessionItems: SessionItem[];
}): SessionItem | undefined => {
  // Find the first item that has a web clip part
  const itemWithWebClip = sessionItems.find(item => {
    const parts = (item.data.map(data => data.part) || []) as AnySessionPart[];
    return parts.some(part => part.kind === SessionPartKind.WebClipV1);
  });

  if (!itemWithWebClip) return undefined;

  // Filter to only include the web clip part
  const webClipData = itemWithWebClip.data.find(
    data => (data.part as AnySessionPart).kind === SessionPartKind.WebClipV1
  );

  if (!webClipData) return undefined;

  return {
    ...itemWithWebClip,
    data: [webClipData],
  };
};

/**
 * Utility function to check if a session feature is enabled
 * If no features array is specified, all features are enabled by default
 */
export const isSessionFeatureEnabled = (
  config: MDSSessionConfig,
  feature: SessionFeature
): boolean => {
  // If no features array is specified, assume all features are enabled
  if (!config.features) {
    return true;
  }

  return config.features.includes(feature);
};
